import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import { Button, ButtonBase, Dialog, DialogContent, DialogTitle, Grid, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { getAnalyzedLinkedinContacts } from 'API/linkedinproject';
import { SortDirection } from 'enums';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import { Attributes, personas } from './profile-generator.types';
import ProjectResultRow from './project-result-row';

interface Props {
  open: boolean;
  projectId?: string;
  filter: string[];
  sorting: { attribute: Attributes; direction: SortDirection } | undefined;

  handleFilterChange(currFilter: string): () => void;

  handleSortChange(attribute: Attributes): () => void;

  handleCloseDialog(): void;
}

export interface ResultCSV {
  url?: string;
  type?: string;
  companyName?: string;
  firstname?: string;
  lastname?: string;
}

const ProjectResultPreview: React.FC<Props> = ({ open, projectId, filter, sorting, handleFilterChange, handleSortChange, handleCloseDialog }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  useUserData();
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const [results, setResults] = useState<{ loading: boolean; objects: ResultCSV[] }>({ loading: false, objects: [] });
  const [typeCount, setTypeCount] = useState();

  useEffect(() => {
    if (!_.isNil(projectId)) {
      (async () => {
        setResults({ ...results, loading: true });

        const res = await getAnalyzedLinkedinContacts(projectId);

        if (res && !res.error) {
          let textArr: string[] = res.split('\n');
          if (!textArr.length) {
            textArr = res.split('\n');
          }

          const columns = _.head(textArr)
            ?.split(';')
            .map((col) => col.replace(/['"]+/g, ''));

          if (!_.isNil(columns)) {
            const contactResults = _.tail(textArr);

            const mappedResults = contactResults.map((result) => {
              const tempObj: any = {};
              const splitResults = result.split(';').map((splitResult) => splitResult.replace(/['"]+/g, ''));

              columns.forEach((col, index) => {
                if (!_.isNil(splitResults[index]) && splitResults[index] !== '') {
                  tempObj[col] = splitResults[index];
                }
              });

              return tempObj;
            });

            setResults({ loading: false, objects: mappedResults });

            const countObj: any = {};
            _.forEach(mappedResults, (obj) => {
              if (!_.isNil(obj.type)) {
                if (_.isNil(countObj[obj.type])) {
                  countObj[obj.type] = 1;
                } else {
                  countObj[obj.type]++;
                }
              }
            });

            let assigned = 0;
            _.forEach(countObj, (value) => {
              assigned += value;
            });
            const notAssigned = mappedResults.length - assigned;
            countObj['notAssigned'] = notAssigned;

            setTypeCount(countObj);
          }
        } else {
          setResults({ ...results, loading: false });
        }
      })();
    }
  }, [projectId]);

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} fullScreen={downLG} maxWidth={'md'}>
      <DialogTitle className={classes.title}>
        <span>{t('preview')}</span>
        <CloseIcon className={classes.closeIcon} onClick={handleCloseDialog} />
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container={true} gap={1}>
          <Grid>
            <ButtonBase className={classes.filterButton} onClick={handleFilterChange('showAll')}>
              <Typography className={cx(classes.filterText, filter.includes('showAll') && 'active')}>
                {t('showAll')} <span style={{ marginLeft: '4px' }}>{`(${results.objects.length})`}</span>
              </Typography>
            </ButtonBase>
          </Grid>

          {personas.map((persona) => (
            <Grid key={persona}>
              <ButtonBase className={classes.filterButton} onClick={handleFilterChange(persona)}>
                <Typography className={cx(classes.filterText, filter.includes(persona) && 'active')}>
                  {persona} <span style={{ marginLeft: '4px' }}>{`(${typeCount?.[persona] || 0})`}</span>
                </Typography>
              </ButtonBase>
            </Grid>
          ))}

          <Grid>
            <ButtonBase className={classes.filterButton} onClick={handleFilterChange('noAssignment')}>
              <Typography className={cx(classes.filterText, filter.includes('noAssignment') && 'active')}>
                {t('noAssignment')} <span style={{ marginLeft: '4px' }}>{`(${typeCount?.['notAssigned'] || 0})`}</span>
              </Typography>
            </ButtonBase>
          </Grid>

          <Grid className={classes.sortContainer} size={12}>
            <Stack direction="row" gap={1} alignItems={'center'} justifyContent={'center'}>
              <Button
                className={cx(
                  classes.sortButton,
                  sorting?.attribute === Attributes.FIRST_NAME && 'active',
                  sorting?.attribute === Attributes.FIRST_NAME && (sorting?.direction === SortDirection.ASC ? 'ascending' : 'descending')
                )}
                onClick={handleSortChange(Attributes.FIRST_NAME)}
                endIcon={<ArrowUpwardIcon />}
                variant={'contained'}
                disableElevation={true}
              >
                {t('firstName')}
              </Button>
              <Button
                className={cx(
                  classes.sortButton,
                  sorting?.attribute === Attributes.LAST_NAME && 'active',
                  sorting?.attribute === Attributes.LAST_NAME && (sorting?.direction === SortDirection.ASC ? 'ascending' : 'descending')
                )}
                onClick={handleSortChange(Attributes.LAST_NAME)}
                endIcon={<ArrowUpwardIcon />}
                variant={'contained'}
                disableElevation={true}
              >
                {t('lastName')}
              </Button>
              <Button
                className={cx(
                  classes.sortButton,
                  sorting?.attribute === Attributes.COMPANY && 'active',
                  sorting?.attribute === Attributes.COMPANY && (sorting?.direction === SortDirection.ASC ? 'ascending' : 'descending')
                )}
                onClick={handleSortChange(Attributes.COMPANY)}
                endIcon={<ArrowUpwardIcon />}
                variant={'contained'}
                disableElevation={true}
              >
                {t('company')}
              </Button>
              <Button
                className={cx(
                  classes.sortButton,
                  sorting?.attribute === Attributes.TYPE && 'active',
                  sorting?.attribute === Attributes.TYPE && (sorting?.direction === SortDirection.ASC ? 'ascending' : 'descending')
                )}
                onClick={handleSortChange(Attributes.TYPE)}
                endIcon={<ArrowUpwardIcon />}
                variant={'contained'}
                disableElevation={true}
              >
                {t('persona')}
              </Button>
            </Stack>
          </Grid>
          {results.loading ? (
            <PaiDotLoader />
          ) : sorting?.direction === SortDirection.DESC ? (
            _.reverse(
              _.sortBy(
                results.objects.filter((obj) => (filter.includes('showAll') ? true : filter.includes(obj.type || 'noAssignment'))),
                (item) => !_.isNil(sorting) && item[sorting.attribute]
              )
            ).map((obj, index) => <ProjectResultRow key={obj.url} resultObject={obj} isEven={index % 2 === 0} />)
          ) : (
            _.sortBy(
              results.objects.filter((obj) => (filter.includes('showAll') ? true : filter.includes(obj.type || 'noAssignment'))),
              (item) => !_.isNil(sorting) && item[sorting.attribute]
            ).map((obj, index) => <ProjectResultRow key={obj.url} resultObject={obj} isEven={index % 2 === 0} />)
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: 0,
  },
  title: {
    background: theme.palette.primary.main,
    color: '#FFF',
    fontSize: theme.typography.pxToRem(18),
    lineHeight: 1,
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
  },
  closeIcon: {
    fontSize: theme.typography.pxToRem(24),
    cursor: 'pointer',
    '&:hover': {
      background: theme.palette.backgrounds.main,
      color: theme.palette.primary.main,
      borderRadius: '50%',
    },
  },
  content: {
    padding: '0 16px',
    margin: '16px 0',
    paddingTop: '0px !important',
    paddingRight: 0,
    overflowX: 'hidden',
    [theme.breakpoints.down(400)]: {
      padding: '0 8px',
      margin: '8px 0',
    },
  },
  sortContainer: {
    margin: '16px 0',
  },
  sortButton: {
    background: '#FFF',
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1,
    padding: '8px 2px',
    width: '125px',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 0,
    maxHeight: '26px',
    '& .MuiSvgIcon-root': {
      display: 'none',
      fontSize: theme.typography.pxToRem(16),
    },
    '&:hover': {
      background: theme.palette.primary.main,
      color: '#FFF',
    },
    '&.active': {
      '& .MuiSvgIcon-root': {
        transition: '225ms',
        display: 'flex',
        right: '8px',
      },
      background: theme.palette.primary.main,
      color: '#FFF',
      '&.descending': {
        '& .MuiSvgIcon-root': {
          transform: 'rotate(180deg)',
        },
      },
    },
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(10),
      '& .MuiSvgIcon-root': {
        fontSize: theme.typography.pxToRem(12),
      },
    },
  },
  loaderContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px 0',
  },
  filterButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterText: {
    color: theme.palette.greys.medium,
    fontSize: theme.typography.pxToRem(12),
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.primary.main,
    },
    '&.active': {
      color: theme.palette.primary.main,
    },
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(10),
    },
  },
}));

export default ProjectResultPreview;
